import { useEffect, useState, useCallback } from 'react';
import useSWR, { mutate as swrMutate } from 'swr';
import { API, V2_API, apiGet, apiPost, isLoggedIn } from './api';
import { MOCK_BADGES } from './mockData';

// Shared SWR config: 60s dedupe + revalidate on focus (snappy after long tabs).
const SWR_OPTS = {
  dedupingInterval: 60_000,
  revalidateOnFocus: true,
  revalidateIfStale: true,
  shouldRetryOnError: false,
};

const fetcher = (url) => apiGet(url);

// Empty state for guests/backend failures — no fake streak or fake walk stats.
function buildEmptyState() {
  return {
    streak: 0, today_km: 0, week_history: [0, 0, 0, 0, 0, 0, 0],
    location_label: null, level: 1, unlocked_badges: [],
    places: [], total_walks: 0,
  };
}

// useMeState — fetches /api/v2/me/state with SWR caching. Dedupes across all
// V2 pages that need the streak/walk data (Home, Me, Map).
export function useMeState() {
  const loggedIn = isLoggedIn();
  const key = loggedIn ? `${V2_API}/me/state` : null;
  const { data, error, isLoading, mutate } = useSWR(key, fetcher, SWR_OPTS);

  if (!loggedIn) {
    return { data: buildEmptyState(), loading: false, usingMock: true, refresh: () => {} };
  }
  if (error) {
    return { data: buildEmptyState(), loading: false, usingMock: true, refresh: mutate };
  }
  return { data: data || null, loading: isLoading, usingMock: false, refresh: mutate };
}

export async function checkInWalk(payload) {
  const result = await apiPost(`${V2_API}/walks/check-in`, payload);
  // Streak/walk count just changed — invalidate the cache so all consumers refresh.
  swrMutate(`${V2_API}/me/state`);
  return result;
}

// useMyPet — fetches the user's first owned animal with SWR.
// Returns: { pet, loading, loggedIn, hasNoPet }
export function useMyPet() {
  const loggedIn = isLoggedIn();
  // Two-stage SWR: first fetch profile to get the user id, then fetch animals.
  const { data: me, error: meErr } = useSWR(
    loggedIn ? `${API}/auth/profile` : null,
    fetcher,
    SWR_OPTS,
  );
  const animalsKey = loggedIn && me?.id ? `${API}/users/${me.id}/animals` : null;
  const { data: animals, error: animalsErr, isLoading: animalsLoading } = useSWR(
    animalsKey, fetcher, SWR_OPTS,
  );

  if (!loggedIn) {
    return { pet: null, loading: false, loggedIn: false, hasNoPet: false };
  }
  if (meErr || animalsErr) {
    return { pet: null, loading: false, loggedIn: true, hasNoPet: false };
  }
  if (!me || (animalsKey && !animals && animalsLoading)) {
    return { pet: null, loading: true, loggedIn: true, hasNoPet: false };
  }
  const owned = Array.isArray(animals)
    ? animals.filter(a => a.animal_type === 'owned' || !a.animal_type)
    : [];
  if (owned.length === 0) {
    return { pet: null, loading: false, loggedIn: true, hasNoPet: true };
  }
  const a = owned[0];
  return {
    pet: {
      name: a.name,
      species: a.species === 'cat' ? 'cat' : 'dog',
      age: a.age || '',
      breed: a.breed || '',
      city: me.city || '',
      traits: [],
      photo: (a.photos && a.photos[0]) || null,
    },
    loading: false,
    loggedIn: true,
    hasNoPet: false,
  };
}

// useMarkers — viewport-scoped pet markers with SWR caching + dedupe.
// Pass a `viewport` object (lat/lng/bounds) — pass null to skip the fetch.
export function useMarkers(viewport, opts = {}) {
  const { limit = 60, includeThumbnail = true } = opts;
  const key = viewport
    ? `${API}/animals/map/markers?` + new URLSearchParams({
        north: String(viewport.north),
        south: String(viewport.south),
        east: String(viewport.east),
        west: String(viewport.west),
        lat: String(viewport.lat),
        lng: String(viewport.lng),
        limit: String(limit),
        include_thumbnail: includeThumbnail ? 'true' : 'false',
      }).toString()
    : null;
  const { data, error, isLoading, mutate } = useSWR(key, fetcher, {
    ...SWR_OPTS,
    dedupingInterval: 15_000, // markers are slightly time-sensitive
  });
  return { markers: Array.isArray(data) ? data : [], error, loading: isLoading, refresh: mutate };
}

// Legacy useEffect-based exports retained for backwards compatibility with any
// component that imports them directly. (No callers currently — kept as a noop
// guard against future regressions.)
export { useState as _useState, useEffect as _useEffect, useCallback as _useCallback };
